import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PlaceCoordsLookup } from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import {
  useGeocodeAutocomplete,
  useGeocodeResolve,
  usePlacesAutocomplete,
} from '@frontend/features/travel-services/queries';
import { PlaceAutocompletePopper } from '@frontend/features/travel-services/components/PlaceAutocomplete';
import { PlaceOptionRow } from '@frontend/features/travel-services/components/PlaceOptionRow';
import { flattenPlaceOptions, PlaceOption } from '@frontend/features/travel-services/places';
import { defaultAttemptIdFactory } from '@frontend/features/travel-services/holdAttemptIdentity';
import { HotelLocation, isTouristDestination, validPoint } from './searchModel';

type Option = { source: 'place'; place: PlaceOption } | { source: 'google'; id: string; label: string };
export function HotelLocationPicker({
  value,
  onChange,
  onResolving,
}: {
  value: HotelLocation | null;
  onChange: (v: HotelLocation | null) => void;
  onResolving: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useDebouncedState('', 250);
  const [input, setInput] = useState(value?.label ?? '');
  const [session, setSession] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [resolving, setResolving] = useState(false);
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  const places = usePlacesAutocomplete(query, {
    kinds: ['city', 'region', 'district', 'neighborhood', 'beach', 'airport', 'poi'],
    group: true,
    enabled: query.trim().length >= 2,
  });
  const google = useGeocodeAutocomplete(query, { sessionToken: session });
  const resolve = useGeocodeResolve();
  const options = useMemo<Option[]>(
    () => [
      ...flattenPlaceOptions(places.data?.results ?? []).map((place) => ({ source: 'place' as const, place })),
      ...(google.data?.predictions ?? []).map((p) => ({
        source: 'google' as const,
        id: p.placeId,
        label: p.description,
      })),
    ],
    [places.data, google.data],
  );
  const label = (option: Option) => (option.source === 'place' ? option.place.name : option.label);
  const clearPending = () => {
    generation.current += 1;
    setError(false);
    setResolving(false);
    onResolving(false);
  };
  const choose = async (option: Option | null) => {
    clearPending();
    onChange(null);
    if (!option) {
      setInput('');
      setQuery('');
      return;
    }
    setInput(label(option));
    setQuery('');
    const attempt = generation.current;
    const sessionToken = session;
    setSession(null);
    if (option.source === 'place' && isTouristDestination(option.place.kind)) {
      onChange({ type: 'destination', placeId: option.place.id, label: option.place.name });
      return;
    }
    setResolving(true);
    onResolving(true);
    try {
      let latitude: number | undefined, longitude: number | undefined;
      let resolutionToken: string | undefined;
      if (option.source === 'place') {
        let coords = option.place.coords;
        if (!coords) {
          const result = (await client.get<PlaceCoordsLookup>(`places/${encodeURIComponent(option.place.id)}/coords`))
            .data;
          if (result.id !== option.place.id) throw new Error('identity');
          coords = result.coords;
        }
        latitude = coords?.lat;
        longitude = coords?.lng;
      } else {
        if (!sessionToken) throw new Error('session');
        const result = await resolve.mutateAsync({ placeId: option.id, sessionToken });
        if (result.placeId !== option.id) throw new Error('identity');
        latitude = result.lat;
        longitude = result.lng;
        resolutionToken = result.resolutionToken;
      }
      if (!validPoint(latitude, longitude)) throw new Error('coordinates');
      if (attempt === generation.current)
        onChange({
          type: 'point',
          latitude: latitude!,
          longitude: longitude!,
          label: label(option),
          ...(option.source === 'place' ? { placeId: option.place.id } : { resolutionToken }),
        });
    } catch {
      if (attempt === generation.current) setError(true);
    } finally {
      if (attempt === generation.current) {
        setResolving(false);
        onResolving(false);
        setSession(null);
      }
    }
  };
  return (
    <Box flex={1} minWidth={250}>
      <Autocomplete<Option, false, false, false>
        options={options}
        value={null}
        inputValue={input}
        filterOptions={(x) => x}
        getOptionLabel={label}
        groupBy={(option) => (option.source === 'google' ? t('hotelSearch.google') : t('hotelSearch.destinations'))}
        onFocus={() => {
          if (!session) setSession(defaultAttemptIdFactory());
        }}
        onInputChange={(_e, text, reason) => {
          if (reason === 'input') {
            clearPending();
            onChange(null);
            setInput(text);
            setQuery(text);
            if (!session) setSession(defaultAttemptIdFactory());
          }
        }}
        onChange={(_e, option) => {
          void choose(option);
        }}
        PopperComponent={PlaceAutocompletePopper}
        loading={resolving || places.isFetching || google.isFetching}
        noOptionsText={t(query.length < 2 ? 'hotelSearch.keepTyping' : 'hotelSearch.noPlaces')}
        renderOption={(props, option) => (
          <li {...props} key={option.source === 'place' ? option.place.id : option.id}>
            {option.source === 'place' ? (
              <Box width="100%">
                <PlaceOptionRow option={option.place} />
                <Typography variant="caption">
                  {t(isTouristDestination(option.place.kind) ? 'hotelSearch.destinationMode' : 'hotelSearch.pointMode')}
                </Typography>
              </Box>
            ) : (
              <Typography>{option.label}</Typography>
            )}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('hotelSearch.location')}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {resolving && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {(places.isError ||
        google.isError ||
        google.data?.provider.reason === 'not_enabled' ||
        google.data?.provider.reason === 'budget_exceeded') && (
        <Typography color="warning.main" variant="caption">
          {t('hotelSearch.partialPlaces')}
        </Typography>
      )}
      {error && <Alert severity="error">{t('hotelSearch.coordsError')}</Alert>}
      {value && (
        <Typography variant="caption">
          {t(value.type === 'destination' ? 'hotelSearch.destinationMode' : 'hotelSearch.pointMode')}
        </Typography>
      )}
    </Box>
  );
}
